// authService.js
import { auth, db } from "../config/firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";

/**
 * Customer self-registration. Creates auth user + Firestore profile
 * with status "pending" until admin approves.
 */
export async function registerCustomer({ email, password, name, companyName, phone }) {
  const cred = await createUserWithEmailAndPassword(auth, email, password);

  await setDoc(doc(db, "users", cred.user.uid), {
    role: "customer",
    email,
    name,
    companyName,
    phone,
    status: "pending",
    createdAt: serverTimestamp(),
    approvedBy: null,
    approvedAt: null
  });

  return cred.user;
}

/**
 * Admin/rep account creation — no self-registration, admin creates these manually
 * via the admin panel (calls this after creating the auth user separately).
 */
export async function createStaffProfile(uid, { email, name, role }) {
  await setDoc(doc(db, "users", uid), {
    role, // "admin" | "rep"
    email,
    name,
    status: "approved",
    createdAt: serverTimestamp()
  });
}

export async function login(email, password) {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  const profile = await getUserProfile(cred.user.uid);

  if (!profile) throw new Error("No profile found for this account.");
  if (profile.role === "customer" && profile.status === "pending") {
    await signOut(auth);
    throw new Error("PENDING_APPROVAL");
  }
  // "revoked" = access cancelled by admin after approval (Customers page).
  // Shown to the customer with the same message as a rejection.
  if (profile.role === "customer" && (profile.status === "rejected" || profile.status === "revoked")) {
    await signOut(auth);
    throw new Error("ACCOUNT_REJECTED");
  }

  return { user: cred.user, profile };
}

export async function logout() {
  await signOut(auth);
}

/**
 * Sends a Firebase password-reset email. Works on the free tier — auth
 * emails are sent by Firebase Auth itself, not by our (nonexistent) mailer.
 */
export async function resetPassword(email) {
  await sendPasswordResetEmail(auth, email);
}

export async function getUserProfile(uid) {
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? snap.data() : null;
}

export function subscribeToAuthChanges(callback) {
  return onAuthStateChanged(auth, async (user) => {
    if (!user) return callback(null, null);
    const profile = await getUserProfile(user.uid);
    callback(user, profile);
  });
}
